import serverContext from 'server-only-context';
import { isDraftPreviewEnabled } from 'utils/draftPreview';
import { NinetailedMode } from 'types/constants';
import { isE2ETesting } from 'utils/environment';
import { decodeExperienceSelections } from 'libs/experimentation/ninetailed-proxy';

export const [getExperiences, setExperiences] = serverContext<Record<string, number> | null>(null);

export const getNinetailedMode: () => Promise<NinetailedMode> = async () => {
  // We use this to override the mode when needed
  // It's handy for local development in .env.local
  // or if we need to override and entire environment like QA
  const overrideMode = process.env.NEXT_PUBLIC_NINETAILED_MODE;

  // If an override mode is set and valid, use it
  if (overrideMode === 'server' || overrideMode === 'client') {
    return overrideMode;
  }

  // Otherwise determine the rendering mode based on whether the env enables draft preview
  return (await isDraftPreviewEnabled()) ? 'client' : 'server';
};

export async function getSlugAndExperiences({ params }: { params: Promise<{ slug: string[] }> }) {
  const rawSlug = (await params)?.slug || [];
  const slug = rawSlug.join('/');

  // importing headers here in-line allows
  // this function to be used both client + server-side
  const { headers } = await import('next/headers');
  const headersList = await headers();

  // Get experience selections from header
  const experienceSelections = headersList.get('x-ninetailed-experiences');
  const selectedExperiences = experienceSelections ? decodeExperienceSelections(experienceSelections) : null;

  return {
    selectedExperiences,
    slug,
  };
}

/**
 * Resolves per-request experimentation state for a page entry.
 *
 * Reads the slug from `params`, decodes the Ninetailed experience selections
 * from the proxy header, stores them in the request-scoped context, and
 * returns the resolved Ninetailed render mode alongside the slug.
 */
export async function setupRequestExperiences({
  params,
}: {
  params: Promise<{ slug: string[] }>;
}): Promise<{ slug: string; ninetailedMode: NinetailedMode }> {
  const ninetailedMode = await getNinetailedMode();
  const { slug, selectedExperiences } = await getSlugAndExperiences({ params });
  setExperiences(selectedExperiences);
  return { slug, ninetailedMode };
}

/**
 * Determines whether to force baseline rendering for Ninetailed experiences.
 * This function centralizes all conditions that should force baseline rendering.
 */
export function shouldRenderBaseline(): boolean {
  // Always render baseline in CI environments to prevent flaky tests or unintended VRT failures
  if (isE2ETesting()) {
    return true;
  }

  // Future conditions can be added here:
  // - Feature flags (e.g., disable personalization for certain users)
  // - User preferences (e.g., opt-out of personalization)
  // - Performance considerations (e.g., disable on slow connections)

  return false;
}
